import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MailOpen, MessagesSquare, LayoutGrid, Users, PiggyBank, ListChecks, Star, ClipboardList } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
export function FeaturesSection() {
  const integrations = [{
    name: 'Gmail',
    logo: 'https://cdn.magicpatterns.com/uploads/i3m9Dz8tVyFX3XYH5aSycT/gmail-icon.svg'
  }, {
    name: 'Outlook',
    logo: 'https://cdn.magicpatterns.com/uploads/wciZK1t5un9NpvyYG4xtuo/outlook-icon.svg'
  }, {
    name: 'iMessage',
    logo: 'https://cdn.magicpatterns.com/uploads/b7Bk8etBDenG3R7oDnec67/imessage-icon.svg'
  }, {
    name: 'WhatsApp',
    logo: 'https://cdn.magicpatterns.com/uploads/j1eFkudG3snN4BnwPPvfbX/whatsapp-icon.svg'
  }, {
    name: 'Google Drive',
    logo: 'https://cdn.magicpatterns.com/uploads/2yqcSmzQqZi9cdSsmgsLpN/google-drive-icon.svg'
  }, {
    name: 'Notion',
    logo: 'https://cdn.magicpatterns.com/uploads/1kGheTjtnQKFkEbYZYttia/notion-icon.svg'
  }, {
    name: 'Airtable',
    logo: 'https://cdn.magicpatterns.com/uploads/bQ3BrNNoSGHPhJ2LPvGch1/airtable-icon.svg'
  }, {
    name: 'Canva',
    logo: 'https://cdn.magicpatterns.com/uploads/iKQEnA2bTqFX8YLk3u7yRT/canva-logo.png'
  }, {
    name: 'OneDrive',
    logo: 'https://cdn.magicpatterns.com/uploads/uQgGV18a3e7tTLcoaM3Puu/onedrive-icon.svg'
  }, {
    name: 'Tripleseat',
    logo: 'https://cdn.magicpatterns.com/uploads/pBvjpazwySPVaj2JeBgC2A/tripleseat-logo.png'
  }];
  const actions = [{
    label: 'Build event identity',
    icon: MailOpen,
    description: 'Naming, branding, and positioning that makes every event feel unmistakably yours.'
  }, {
    label: 'Run guest outreach',
    icon: MessagesSquare,
    description: 'Personalized invites and follow-ups that fill the room with the right people.'
  }, {
    label: 'Manage venues',
    icon: LayoutGrid,
    description: 'Sourcing, contracts, and logistics handled end to end.'
  }, {
    label: 'Prep your hosts',
    icon: Users,
    description: 'Run-of-show, talking points, and briefings so your team shows up ready.'
  }, {
    label: 'Co-host with you',
    icon: Star,
    description: 'We are in the room on the day, running the event alongside your team.'
  }, {
    label: 'Sync with platforms',
    icon: PiggyBank,
    description: 'Connect directly to Luma, Partiful, Posh, and other event platforms.'
  }, {
    label: 'Run from anywhere',
    icon: ListChecks,
    description: 'Manage every event from the tools your agency already uses.'
  }, {
    label: 'Curate guest lists',
    icon: ClipboardList,
    description: 'Build the right room for every event so your clients see real ROI.'
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const pillVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  return <section id="features" className="py-20 bg-white" data-id="element-128">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-129">
        {/* Integrations Carousel */}
        <AnimatedSection data-id="element-130">
          <div className="text-center mb-16" data-id="element-131">
            <p className="text-sm font-semibold text-indigo-600 tracking-wide uppercase mb-8" data-id="element-132">
              Full-service event planning for lean agencies
            </p>
            <div className="relative overflow-hidden" data-id="element-133">
              <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" data-id="element-134" />
              <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" data-id="element-135" />
              <div className="flex animate-marquee hover:[animation-play-state:paused]" data-id="element-136">
                {[...integrations, ...integrations].map((item, index) => <div key={index} className="flex items-center space-x-3 px-8 flex-shrink-0" data-id="element-137">
                    <img src={item.logo} alt={item.name} className="h-8 w-8 object-contain opacity-70" data-id="element-138" />
                    <span className="font-medium text-gray-500 whitespace-nowrap" data-id="element-139">
                      {item.name}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Features Heading */}
        <AnimatedSection delay={0.2} data-id="element-140">
          <div className="text-center mb-12" data-id="element-141">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-142">
              Offer Events Without Building an Events Team
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-143">
              Turnout handles event identity, guest outreach, venues, host prep,
              and co-hosting, so your small team can deliver big events as a new
              go-to-market motion.
            </p>
          </div>
        </AnimatedSection>

        {/* Action Pills Grid — staggered entrance */}
        <motion.div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} data-id="element-144">
          {actions.map((action, index) => {
          const Icon = action.icon;
          return <motion.div key={index} variants={pillVariants} whileHover={{
            scale: 1.02,
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} className="group flex items-center gap-5 px-6 py-5 bg-white border border-indigo-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-colors duration-200 cursor-default" data-id="element-145">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center transition-colors duration-200" data-id="element-146">
                  <Icon className="h-6 w-6 text-indigo-600" data-id="element-147" />
                </div>
                <div className="min-w-0" data-id="element-148">
                  <span className="text-lg font-semibold text-gray-900 block leading-tight" data-id="element-149">
                    {action.label}
                  </span>
                  <span className="text-sm text-gray-500 leading-snug block mt-0.5" data-id="element-150">
                    {action.description}
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}